import { useMemo } from 'react';
import { isValidDateInput } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';

export type LinkedTechnician = {
  name: string;
  id: string;
  value: string;
};
export type LinkedTechniciansGroup = {
  allTechnicians: LinkedTechnician[];
  scheduledTechnicians: LinkedTechnician[];
  nonScheduledTechnicians: LinkedTechnician[];
};

export const useLocationLinkedTechnicians = ({
  serviceLocationId,
  startTime,
  endTime,
  assigningToAJob,
}: {
  serviceLocationId: string;
  startTime?: string;
  endTime?: string;
  assigningToAJob?: boolean;
}) => {
  const hasValidStartTime = isValidDateInput(startTime);
  const hasValidEndTime = isValidDateInput(endTime);
  const hasValidTimeRange = hasValidStartTime && hasValidEndTime;

  const { data: technicians, isLoading: isLoadingTechnicians } =
    trpc.serviceLocation.getLocationLinkedTechnicianUsers.useQuery(
      {
        serviceLocationId: serviceLocationId,
        ...(hasValidTimeRange
          ? { scheduleStart: startTime, scheduleEnd: endTime }
          : {}),
        coverTheRange: assigningToAJob,
      },
      {
        enabled: !!serviceLocationId && hasValidTimeRange,
      }
    );

  const sortedTechnicians = useMemo(() => {
    const allTechnicians: LinkedTechnician[] =
      technicians?.map((user) => ({
        value: `${user.firstName} ${user.lastName}`,
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
      })) ?? [];
    const scheduledTechnicians: LinkedTechnician[] =
      technicians
        ?.filter((user) => user.isScheduled)
        .map((user) => ({
          value: `${user.firstName} ${user.lastName}`,
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        })) ?? [];
    const nonScheduledTechnicians: LinkedTechnician[] =
      technicians
        ?.filter((user) => !user.isScheduled)
        .map((user) => ({
          value: `${user.firstName} ${user.lastName}`,
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        })) ?? [];
    return {
      allTechnicians,
      scheduledTechnicians,
      nonScheduledTechnicians,
    };
  }, [technicians]);

  return {
    technicians: sortedTechnicians,
    isLoadingTechnicians,
  };
};
